import { Box, Checkbox, FormControlLabel, Grid }                   from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';

import {
  FastField,
  Field,
  FormikProps,
  useFormikContext,
}                                      from 'formik';
import {
  FC,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
}                                      from 'react';
import styled                          from 'styled-components';
import { ReactComponent as IconEvent } from '../../../../../images/icons/icon-landscape.svg';
import { documentAccessLevels }        from '../../../constants/constants';
import { CheckboxField }               from '../../../formFields/CheckboxField';
import { DateRangeField }              from '../../../formFields/DateRangeField';
import { FamilyMembersSelector }       from '../../../formFields/FamilyMembersSelector';
import { SelectField }                 from '../../../formFields/SelectField';
import { SelectTimeField }             from '../../../formFields/SelectTimeField';
import { TextInputField }              from '../../../formFields/TextInputField';
import { WithCategorySelection }       from '../../../hocs/withCategorySelection';
import { useFetchCategories }          from '../../../queries/categories';
import { StyledDivider }               from '../../../styledComponents';
import { ColorPickerDropdown }         from '../../ColorPicker/ColorPickerDropdown';
import { DocumentPicker }              from '../../documents/DocumentPicker';
import {
  UploadEntityFormDropdownField,
  UploadEntityFormLabel,
  UploadEntityFormRow,
  UploadEntityFormSpacing,
}                                      from '../../documents/styles';
import { GridDivider }                 from '../../layout/GridDivider';
import { RepeatOptions }               from '../../RepeatOptions/RepeatOptions';
import {
  ERecurrence,
  IRepeatOptions,
}                                      from '../shared';
import { ModalFooterActions }          from '../shared/ModalFooterActions';
import { IUploadEvent }                from './queries';
import { IEvent }                      from '../../../../modules/Schedule/queries';
import { LabelText }                   from '../../../../Auth/shared/styles';

interface IAddEventModalFormProps {
  familyId?: string;
  schedule?: Array<IEvent>;
  loading  : boolean;
  files    : File[];
  event    : any;
  formikRef: RefObject<FormikProps<IUploadEvent> | null>;
  disableRecursField?: boolean;
  onFileAdd(files: File[]): void;
}

const GridTimeWrapper = styled(Grid)`
  display: flex;
  align-items: center;
  margin-top: 8px !important;
  justify-content: center;
`;

const TimeBlock = styled(Grid)`
  position: relative;
`;

const ErrorTextBlock = styled('p')`
  color: #f44336;
  font-size: 0.7rem;
`;

const DescriptionField = styled(FastField)(({ theme }: any) => ({
  'textarea': {
    height: '0px !important',
  },
}));

export const UploadEventModalForm: FC<IAddEventModalFormProps> = ({
  familyId,
  schedule,
  event,
  loading,
  files,
  onFileAdd,
  formikRef,
  disableRecursField,
}) => {
  const { values, setFieldValue, errors } = useFormikContext<IUploadEvent>();
  const { data: categories, isLoading: loadingCategories } = useFetchCategories(familyId);
  const [timePickerShow, setTimePieckerShown] = useState(!values.allDay);
  const [endTimePickerShow, setEndTimePieckerShown] = useState(Boolean(values.endDate));
  const [ErrorText, setErrorText] = useState<null | string>(null);

  useEffect(() => {
    if(!endTimePickerShow) {
      setFieldValue('endDate', '');
      setErrorText(null);
    }
  }, [endTimePickerShow]);

  useEffect(() => {
    if (values?.recursStartTime && values?.endDate) {

      const startDate = new Date(values.recursStartTime);
      const endDate = new Date(values.endDate);

      if ((endDate > startDate) || (endDate == startDate) && !values?.recursEndDate) {
        setErrorText(null);
      } else if ((endDate > startDate) || (endDate < startDate) && values?.recursEndDate) {
        setErrorText(null);
      } else {
        setErrorText('End date must be after start date.');
      }
    }
  }, [values?.recursStartTime, values?.endDate, values?.recursEndDate, errors]);

  useEffect(
    () => {
      !!values.allDay ? setTimePieckerShown(false) : setTimePieckerShown(true);
    },
    [values.allDay]
  );

  useEffect(() => {
    if (values.allDay) {
      setFieldValue('recursStartTime', '');
      setFieldValue('endDate', '');
      setTimePieckerShown(false);
      setEndTimePieckerShown(false);
    } else {
      setTimePieckerShown(true);
    }
  }, [values.allDay]);

  useEffect(() => {
    if(event?.endDate) {
      setFieldValue('endDate', event.endDate);
      setEndTimePieckerShown(true);
    } else {
      setFieldValue('endDate', '');
    }
  }, [event]);

  // Refs for mutable values used inside the stable Form closure
  const loadingRef            = useRef(loading);
  loadingRef.current          = loading;

  const filesRef              = useRef(files);
  filesRef.current            = files;

  const onFileAddRef          = useRef(onFileAdd);
  onFileAddRef.current        = onFileAdd;

  const scheduleRef           = useRef(schedule);
  scheduleRef.current         = schedule;

  const timePickerShowRef     = useRef(timePickerShow);
  timePickerShowRef.current   = timePickerShow;

  const endTimePickerShowRef  = useRef(endTimePickerShow);
  endTimePickerShowRef.current = endTimePickerShow;

  const ErrorTextRef          = useRef(ErrorText);
  ErrorTextRef.current        = ErrorText;

  const disableRecursRef      = useRef(disableRecursField);
  disableRecursRef.current    = disableRecursField;

  const Form = useMemo(() => WithCategorySelection(function EventFormInner({
    categoriesDropdown,
  }) {
    const {
      values    : formValues,
      setFieldValue: setFV,
      handleBlur,
      handleChange,
      errors    : formErrors,
    } = useFormikContext<IUploadEvent>();

    const repeatOptions = {
      recurs          : formValues.recurs || ERecurrence.NoRecurrence,
      recursInterval  : formValues.recursInterval,
      recursDay       : formValues.recursDay,
      recursOrdinal   : formValues.recursOrdinal,
      recursWeekDays  : formValues.recursWeekDays,
      recursCount     : formValues.recursCount,
      recursStartDate : formValues.recursStartDate,
      recursEndDate   : formValues.recursEndDate,
    };

    const updateRepeatOptions = (options: IRepeatOptions) => {
      setFV('recurs', options?.recurs);
      setFV('recursInterval', options?.recursInterval);
      setFV('recursDay', options?.recursDay);
      setFV('recursOrdinal', options?.recursOrdinal);
      setFV('recursWeekDays', options?.recursWeekDays);
      setFV('recursCount', options?.recursCount);

      if (formValues?.recursEndDate !== options?.recursEndDate) {
        setFV('recursEndDate', options?.recursEndDate);
      }
    };

    return (
      <UploadEntityFormSpacing>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            <FastField
              label="Event Title"
              name={'title'}
              inputProps={{ maxLength: 100 }}
              component={TextInputField}
            />

            <GridDivider margin="20px 0 0" />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Category</UploadEntityFormLabel>

              <UploadEntityFormDropdownField>
                { categoriesDropdown }
              </UploadEntityFormDropdownField>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Assign</UploadEntityFormLabel>

              <FamilyMembersSelector
                multiple
                hideAllSelection
                name="assigned"
              />
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel width="135px">Assignee Permissions</UploadEntityFormLabel>

              <UploadEntityFormDropdownField>
                <FastField
                  name="assignedPermission"
                  options={documentAccessLevels}
                  component={SelectField}
                />
              </UploadEntityFormDropdownField>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Tag <i>Optional</i></UploadEntityFormLabel>

              <FamilyMembersSelector
                multiple
                hideAllSelection
                name="tagged"
              />
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Due Date</UploadEntityFormLabel>

              <GridTimeWrapper container spacing={2}>
                <Grid2 xs={12} sm={4.7}>
                  <FastField
                    startName="recursStartDate"
                    endName="recursEndDate"
                    component={DateRangeField}
                  />
                </Grid2>

                <Grid item xs={12} sm={3}>
                  <Field
                    readOnly
                    label="All Day"
                    name="allDay"
                    component={CheckboxField}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  {timePickerShowRef.current && (
                    <FormControlLabel
                      label={<LabelText>End Time</LabelText>}
                      control={
                        <Checkbox
                          checked={endTimePickerShowRef.current || !!formValues?.endDate}
                          onChange={() => setEndTimePieckerShown(!endTimePickerShowRef.current)}
                        />
                      }
                    />
                  )}
                </Grid>

                {timePickerShowRef.current && (
                  <TimeBlock item xs={9} sm={3}>
                    <Field
                      name="recursStartTime"
                      component={SelectTimeField}
                      schedule={scheduleRef.current}
                    />

                    {(endTimePickerShowRef.current || (formValues?.endDate && endTimePickerShowRef.current)) && (
                      <>
                        <Field
                          name="endDate"
                          component={SelectTimeField}
                          schedule={scheduleRef.current}
                        />
                        <ErrorTextBlock>{formErrors.recursStartTime ? formErrors.recursStartTime : ErrorTextRef.current}</ErrorTextBlock>
                      </>
                    )}
                  </TimeBlock>
                )}

                <Grid item xs={2} sm={1}>
                  <RepeatOptions
                    values={repeatOptions}
                    disabled={disableRecursRef.current}
                    onChange={updateRepeatOptions}
                  />
                </Grid>

              </GridTimeWrapper>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <FastField
              fullWidth
              label={
                <span>
                  Add Location <i style={{ marginLeft: '5px' }}>Optional</i>
                </span>
              }
              name="location"
              inputProps={{ maxLength: 100 }}
              component={TextInputField}
            />

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormRow>
              <UploadEntityFormLabel>Set Color</UploadEntityFormLabel>

              <UploadEntityFormDropdownField>
                <ColorPickerDropdown name="color" />
              </UploadEntityFormDropdownField>
            </UploadEntityFormRow>

            <StyledDivider
              mt={20}
              mb={0}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <DescriptionField
              multiline
              fullWidth
              rows={4}
              label={
                <span>
                  Description <i style={{ marginLeft: '5px' }}>Optional</i>
                </span>
              }
              name={'description'}
              inputProps={{ maxLength: 100 }}
              component={TextInputField}
            />
          </Grid>

          <GridDivider margin="-5px 0" />

          <Grid
            item
            xs={12}
          >
            <UploadEntityFormLabel>Attach File<i>Optional</i></UploadEntityFormLabel>

            <Box height={10} />

            <DocumentPicker
              multiple
              icon={<IconEvent />}
              files={filesRef.current}
              onChange={(f) => onFileAddRef.current(f as File[])}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <ModalFooterActions
              formikRef={formikRef}
              disabled={loadingRef.current || !filesRef.current}
            >
              { loadingRef.current ? 'Uploading' : formValues?.id ? 'Edit' : 'Add' } Event
            </ModalFooterActions>
          </Grid>
        </Grid>
      </UploadEntityFormSpacing>
    );
  }), []);

  return useMemo(() => (
    <Form
      values={values}
      familyId={familyId}
      categories={categories}
      loading={loadingCategories}
      setFieldValue={setFieldValue}
    />
  ), [
    files,
    loading,
    categories,
    values?.recursStartDate,
    values?.recursEndDate,
    values?.alertInterval,
    values?.categoryId,
    values?.recursOrdinal,
    values?.recursDay,
    values?.allDay,
    values?.endDate,
    timePickerShow,
    endTimePickerShow,
    values?.recurs,
    ErrorText,
  ]);
};
